/**
 * Generates an idempotency key with graceful fallback across environments.
 * Preference order:
 * 1) crypto.randomUUID()
 * 2) crypto.getRandomValues() RFC4122 v4-like UUID
 * 3) Math.random() UUID-like fallback (non-cryptographic)
 *
 * @author sadatmalik
 */
export function generateIdempotencyKey(): string {
  const maybeCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;

  if (maybeCrypto && typeof maybeCrypto.randomUUID === 'function') {
    return maybeCrypto.randomUUID();
  }

  if (maybeCrypto && typeof maybeCrypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    maybeCrypto.getRandomValues(bytes);

    // RFC 4122 section 4.4: set version and variant bits.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20)
    ].join('-');
  }

  const randomSegment = () => Math.random().toString(16).slice(2, 10).padEnd(8, '0');
  return [
    randomSegment(),
    randomSegment().slice(0, 4),
    randomSegment().slice(0, 4),
    randomSegment().slice(0, 4),
    randomSegment() + randomSegment()
  ].join('-');
}
